import { useState, useEffect, useRef } from 'react';
import { ArrowDropDown, ArrowDropUp, Check, Close, QuestionMark } from '@mui/icons-material';
import './Dropdown.css';


const defaultOptions = [
    {
        name: 'Option 1',
        value: 'option1',
        description: 'optional',
        helpModal: null
    }
]

interface DropdownOption {
    name: string,
    value: string,
    description: string,
    helpModal: any
}


function Dropdown(
    {setDropdownValue, dropdownValue=null, options=defaultOptions, placeholder="Select"} :
    {setDropdownValue: any, dropdownValue: string|null, options: Array<DropdownOption>|Promise<Array<DropdownOption>>, placeholder: string}
)
{
    let [value, setValue] = useState<{label:string, value:null|string}>(
        {
            label: (options instanceof Promise ? placeholder : options.filter(v => v.value === dropdownValue)[0]?.name) || placeholder,
            value: dropdownValue
        });
    let dropdownRef = useRef<HTMLDivElement>(null);
    let [open, setOpen] = useState(false);
    let [dropdownHeight, setDropdownHeight] = useState(0);
    let [resolvedOptions, setOptions] = useState(options);

    const hasDescriptions = options instanceof Array ? options.some(option => option.description) : true;

    useEffect(() => {
        if (options instanceof Promise) {
            options.then((opts) => setOptions(opts));
        }
    })

    const toggleDropdown = () => {
        if (!open && dropdownRef.current) {
            let boundingRect = dropdownRef.current.getBoundingClientRect();
            // If the space below the dropdown is less than the max height of the dropdown body, resize it.
            if (window.innerHeight - boundingRect.bottom < (window.innerHeight / 2)) {
                setDropdownHeight(window.innerHeight - boundingRect.bottom);
            }
        }

        setOpen(!open);
    }

    const handleClick = (e:any) => {
        if (e.target !== dropdownRef.current && !dropdownRef.current?.contains(e.target)) {
            setOpen(false);
        }
    }

    useEffect(() => {
        document.addEventListener('click', handleClick);

        return () => document.removeEventListener('click', handleClick);
    })

    const openWindow = (element:any, e:any) => {
        e.stopPropagation();

        let newWindow = window.open(
            "",
            "",
            "width=600,height=400,left=450,top=0"
        );

        if (!newWindow) return;

        let container = newWindow.document.createElement('div');
        container.style.padding = "10px";
        let title = newWindow.document.createElement('h1');
        title.innerText = element.name;
        let documentTitle = newWindow.document.createElement('title');
        documentTitle.innerText = "Details - " + element.name;
        newWindow.document.head.appendChild(documentTitle);

        let content = newWindow.document.createElement('div');
        content.innerHTML = element.description;
        content.style.whiteSpace = "pre-wrap";

        container.appendChild(title);
        container.appendChild(content);

        // Append container
        newWindow?.document.body.appendChild(container);

        // Transfer styles
        Array.from(document.styleSheets).forEach((styleSheet) => {
            const styleElement = styleSheet?.ownerNode?.cloneNode(true);
            if (styleElement) {
                (styleElement as any).href = styleSheet.href;
                newWindow?.document.head.appendChild(styleElement);
            }
        });      
        Array.from(document.fonts).forEach((font) => newWindow?.document.fonts.add(font));
    
    }

    return (
        <div ref={dropdownRef} className={open ? "dropdown open" : "dropdown"} onClick={toggleDropdown} >
            <div className="dropdownInitial">
                {value.label}
                {open ? <ArrowDropUp /> : <ArrowDropDown />}
            </div>
            <div className={hasDescriptions ? "options descriptions" : "options"} style={{height: dropdownHeight || ""}} onClick={(e) => e.stopPropagation()}>
                <h3 className="mobile">{placeholder}</h3>
                {value.value !== null && !dropdownValue && <div
                    className="dropdownOption dropdownOptionPlaceholder"
                    tabIndex={0}
                    onClick={() => {setValue({label: placeholder, value: null}); toggleDropdown(); setDropdownValue(null)}}>
                        <Close />
                        <span style={{flexGrow: '1', fontWeight: "bold"}}>Reset {hasDescriptions ? placeholder : ''}</span>
                </div>}
                {
                    resolvedOptions instanceof Promise ? <p>Loading...</p> :
                    resolvedOptions.map((option, index) => (
                        <div
                            className="dropdownOption"
                            key={index}
                            tabIndex={0}
                            style={{backgroundColor: value.value === option.value ? '#e0e0e0' : undefined}}
                            onClick={() => {setValue({label: option.name, value: option.value}); toggleDropdown(); setDropdownValue(option.value)}}>
                                <span>
                                    {option.description ? <h3>{option.name}</h3> : option.name}
                                    <span className="desc" dangerouslySetInnerHTML={{__html: option.description}}></span>
                                </span>
                                {option.helpModal ? <button onClick={openWindow.bind(document, option.helpModal)}>
                                    <QuestionMark />
                                </button> : null}
                                {value.value === option.value && <Check />}
                        </div>
                    ))
                }
                <div className="btn mobile" onClick={() => setOpen(false)}>
                    <Close />
                    <span>Close</span>
                </div>
            </div>
        </div>
    )
}

export default Dropdown;